import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import PageLayout from "components/PageLayout";
import "styles/BasicInformation.css";

import {
  fetchDepartmentOptions,
  fetchJobRoleOptions,
  fetchPurposeOptions,
  saveUserData,
} from "utils/api";
import { getuserId, getUserProfile } from "utils/auth";

const LOGO_SRC =
  "/static/media/BiosciencesAndTechnology.68f99ceb334dfcde911552aca53e4fe9.svg";

const selectStyles = {
  control: (base) => ({
    ...base,
    backgroundColor: "#fff",
    border: "1px solid #cfcfcf",
    borderRadius: 4,
    minHeight: 40,
    boxShadow: "none",
    ":hover": { borderColor: "#bdbdbd" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, color: "#8a8a8a" }),
  dropdownIndicator: (base) => ({ ...base, color: "#8a8a8a" }),
  clearIndicator: (base) => ({ ...base, color: "#8a8a8a" }),
  menu: (base) => ({
    ...base,
    zIndex: 9,
    border: "1px solid #cfcfcf",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 220,
  }),
  option: (base, state) => ({
    ...base,
    padding: "6px 10px",
    backgroundColor: state.isSelected
      ? "rgba(80,0,0,.12)"
      : state.isFocused
      ? "rgba(80,0,0,.06)"
      : "#fff",
    color: "#111",
    cursor: "pointer",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "rgba(80,0,0,.1)",
  }),
  multiValueRemove: (base) => ({
    ...base,
    ":hover": { backgroundColor: "transparent", color: "#500000" },
  }),
};

const CheckboxOption = (props) => {
  const { isSelected, isFocused, innerProps, children } = props;
  return (
    <div
      {...innerProps}
      className="bi-option"
      data-focused={isFocused ? "true" : "false"}
      data-selected={isSelected ? "true" : "false"}
    >
      <span className={`bi-option-box ${isSelected ? "on" : ""}`} />
      <span>{children}</span>
    </div>
  );
};

const BasicInformation = ({ onSuccess }) => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm({ mode: "all" });

  const departmentValues = watch("department") || [];
  const jobRoleValues = watch("jobRole") || [];
  const purposeValues = watch("purpose") || [];

  const showOtherDepartment = departmentValues.some((v) => v?.value === "Other");
  const showOtherJobRole = jobRoleValues.some((v) => v?.value === "Other");
  const showOtherPurpose = purposeValues.some((v) => v?.value === "Other");

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [jobRoleOptions, setJobRoleOptions] = useState([]);
  const [purposeOptions, setPurposeOptions] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [deps, roles, purps] = await Promise.all([
          fetchDepartmentOptions(),
          fetchJobRoleOptions(),
          fetchPurposeOptions(),
        ]);
        setDepartmentOptions(deps.map((x) => ({ value: x, label: x })));
        setJobRoleOptions(roles.map((x) => ({ value: x, label: x })));
        setPurposeOptions(purps.map((x) => ({ value: x, label: x })));
      } catch (e) {
        console.error("Error loading options:", e);
      }
    };
    load();
  }, []);

  const alphaSpacePattern = /^[A-Za-z\s]+$/;

  const onSubmit = async (data) => {
    try {
      const mapOther = (arr, otherValue) => {
        const vals = arr.map((x) => x.value);
        const idx = vals.indexOf("Other");
        if (idx > -1 && otherValue) vals[idx] = otherValue;
        return vals;
      };

      const departments = mapOther(data.department || [], data.otherDepartment);
      const jobRoles = mapOther(data.jobRole || [], data.otherJobRole);
      const purposes = mapOther(data.purpose || [], data.otherPurpose);

      const profile = await getUserProfile();
      if (!profile) throw new Error("User profile not found");

      const user = {
        userId: getuserId(),
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        departments,
        jobRoles,
        purposeForAccess: purposes,
        workPhoneNumber: data.workPhone,
        mobilePhoneNumber: data.mobilePhone || "",
        advisor: data.advisor || "",
        homeAddressLine1: data.addressLine1 || "",
        homeAddressLine2: data.addressLine2 || "",
        city: data.city || "",
        state: data.state || "",
        zipCode: data.zipCode || "",
        birthdate: data.birthdate || "",
        acceptedTermsAndConditions: true,
      };

      await saveUserData(user);
      onSuccess?.();
      navigate("/success");
    } catch (e) {
      console.error("Submit error:", e);
      alert("There was an error submitting the form.");
    }
  };

  return (
    <PageLayout>
      <div className="bi-paper">
        <div className="tc-header">
          <img
            src={LOGO_SRC}
            alt="Institute of Biosciences and Technology"
            className="logo-image"
          />
        </div>

        <div className="tc-divider" />

        <h2 className="bi-title">Basic Information</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="bi-form">
          <div className="bi-field">
            <label className="bi-label">
              Department<span className="bi-req">*</span>
            </label>
            <Controller
              name="department"
              control={control}
              rules={{ required: "Please select at least one department." }}
              render={({ field }) => (
                <Select
                  {...field}
                  isMulti
                  placeholder="Select..."
                  options={departmentOptions}
                  styles={selectStyles}
                  components={{ Option: CheckboxOption }}
                />
              )}
            />
            {errors.department && <p className="bi-err">{errors.department.message}</p>}
            {showOtherDepartment && (
              <div className="bi-other">
                <label>
                  Other Department<span className="bi-req">*</span>
                </label>
                <input
                  type="text"
                  {...register("otherDepartment", {
                    required: "Please specify the other department",
                    minLength: { value: 15, message: "Department name must be at least 15 characters" },
                    maxLength: { value: 50, message: "Department name must not exceed 50 characters" },
                    pattern: { value: alphaSpacePattern, message: "Only letters and spaces are allowed" },
                  })}
                />
                {errors.otherDepartment && <p className="bi-err">{errors.otherDepartment.message}</p>}
              </div>
            )}
          </div>

          <div className="bi-field">
            <label className="bi-label">
              Job Role<span className="bi-req">*</span>
            </label>
            <Controller
              name="jobRole"
              control={control}
              rules={{ required: "Please select at least one job role." }}
              render={({ field }) => (
                <Select
                  {...field}
                  isMulti
                  placeholder="Select..."
                  options={jobRoleOptions}
                  styles={selectStyles}
                  components={{ Option: CheckboxOption }}
                />
              )}
            />
            {errors.jobRole && <p className="bi-err">{errors.jobRole.message}</p>}
            {showOtherJobRole && (
              <div className="bi-other">
                <label>
                  Other Job Role<span className="bi-req">*</span>
                </label>
                <input
                  type="text"
                  {...register("otherJobRole", {
                    required: "Please specify the other job role",
                    minLength: { value: 15, message: "Job role must be at least 15 characters" },
                    maxLength: { value: 50, message: "Job role must not exceed 50 characters" },
                    pattern: { value: alphaSpacePattern, message: "Only letters and spaces are allowed" },
                  })}
                />
                {errors.otherJobRole && <p className="bi-err">{errors.otherJobRole.message}</p>}
              </div>
            )}
          </div>

          <div className="bi-field">
            <label className="bi-label">
              Purpose for Access<span className="bi-req">*</span>
            </label>
            <Controller
              name="purpose"
              control={control}
              rules={{ required: "Please select at least one purpose." }}
              render={({ field }) => (
                <Select
                  {...field}
                  isMulti
                  placeholder="Select..."
                  options={purposeOptions}
                  styles={selectStyles}
                  components={{ Option: CheckboxOption }}
                />
              )}
            />
            {errors.purpose && <p className="bi-err">{errors.purpose.message}</p>}
            {showOtherPurpose && (
              <div className="bi-other">
                <label>
                  Other Purpose<span className="bi-req">*</span>
                </label>
                <input
                  type="text"
                  {...register("otherPurpose", {
                    required: "Please specify the other purpose",
                    minLength: { value: 15, message: "Purpose must be at least 15 characters" },
                    maxLength: { value: 50, message: "Purpose must not exceed 50 characters" },
                    pattern: { value: alphaSpacePattern, message: "Only letters and spaces are allowed" },
                  })}
                />
                {errors.otherPurpose && <p className="bi-err">{errors.otherPurpose.message}</p>}
              </div>
            )}
          </div>

          <div className="bi-field">
            <label className="bi-label">
              Advisor<span className="bi-req" aria-hidden="true" />
            </label>
            <input
              type="text"
              placeholder="Advisor"
              {...register("advisor", { setValueAs: (v) => v?.trim() || "" })}
            />
          </div>

          <div className="bi-field">
            <label className="bi-label">
              Work Phone Number<span className="bi-req">*</span>
            </label>
            <input
              type="text"
              placeholder="123-456-7890"
              {...register("workPhone", {
                required: "Work phone number is required",
                pattern: { value: /^\d{3}-\d{3}-\d{4}$/, message: "Phone must be in format XXX-XXX-XXXX" },
              })}
            />
            {errors.workPhone && <p className="bi-err">{errors.workPhone.message}</p>}
          </div>
          
          <div className="bi-actions">
            <button type="submit" className="bi-submit" disabled={!isValid}>
              SUBMIT
            </button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};

export default BasicInformation;
